import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import axiosInstance from '@/lib/enhancedAxiosInstance';
import { paths } from '@/lib/apiPaths';
import { ENABLE_CUSTOM_ATTRIBUTES } from '@/config/featureFlags';
import { useAuth } from '@/contexts/AuthContext';
import { usePriceMetadata } from '@/hooks/usePriceMetadata';
import { useAttributesTranslations } from '@/hooks/useAttributesTranslations';
import { useTranslation } from 'react-i18next';
import { useInvalidateAttributesOnlyCache } from '@/hooks/useGlobalAttributes';
import { translateAttributeType } from '@/utils/attributeTypeTranslations';
import { logError } from '@/utils/errorHandler';

// Enhanced components
import { EnhancedAttributeModal } from '@/features/attributes/EnhancedAttributeModal';

// UI Components
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import {
  PlusCircle,
  AlertCircle,
  Pencil,
  Trash,
  Check,
  Settings,
  Ruler,
  DollarSign,
  FileText,
  Image,
  Globe,
  Monitor,
} from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '@/components/ui/alert-dialog';

import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { Skeleton } from '@/components/ui/skeleton';

// Enhanced attribute interface
interface EnhancedAttribute {
  id: number;
  code: string;
  label: string;
  data_type: string;
  is_localisable: boolean;
  is_scopable: boolean;
  is_required: boolean;
  validation_rule?: string;
  allowed_units?: string[];
  default_unit?: string;
  default_currency?: string;
  allowed_html_tags?: string[];
  allowed_file_types?: string[];
  max_file_size?: number;
  allow_negative_numbers?: boolean;
  organization: number;
  created_by: number;
  is_core?: boolean;
  in_use?: boolean;
  options?: Array<{ id: number; value: string; label: string; order: number }>;
}

// Enhanced attribute form data interface
interface AttributeFormData {
  code: string;
  label: string;
  data_type: string;
  is_localisable: boolean;
  is_scopable: boolean;
  is_required: boolean;
  validation_rule: string;
  allowed_units: string[];
  default_unit: string;
  default_currency: string;
  allowed_html_tags: string[];
  allowed_file_types: string[];
  max_file_size: number;
  allow_negative_numbers: boolean;
  options: Array<{ value: string; label: string; order: number }>;
}

// Get attribute type icon
const getAttributeTypeIcon = (type: string) => {
  switch (type) {
    case 'text':
      return FileText;
    case 'number':
      return Settings;
    case 'boolean':
      return Check;
    case 'date':
      return Settings;
    case 'measurement':
      return Ruler;
    case 'price':
      return DollarSign;
    case 'rich_text':
      return FileText;
    case 'media':
      return Image;
    default:
      return Settings;
  }
};

// Get attribute type color
const getAttributeTypeColor = (type: string) => {
  switch (type) {
    case 'text':
      return 'bg-blue-50 text-blue-700 border-blue-200';
    case 'number':
      return 'bg-amber-50 text-amber-700 border-amber-200';
    case 'boolean':
      return 'bg-green-50 text-green-700 border-green-200';
    case 'date':
      return 'bg-purple-50 text-purple-700 border-purple-200';
    case 'measurement':
      return 'bg-indigo-50 text-indigo-700 border-indigo-200';
    case 'price':
      return 'bg-emerald-50 text-emerald-700 border-emerald-200';
    case 'select':
      return 'bg-orange-50 text-orange-700 border-orange-200';
    case 'rich_text':
      return 'bg-pink-50 text-pink-700 border-pink-200';
    case 'media':
      return 'bg-cyan-50 text-cyan-700 border-cyan-200';
    case 'url':
      return 'bg-teal-50 text-teal-700 border-teal-200';
    case 'email':
      return 'bg-red-50 text-red-700 border-red-200';
    case 'phone':
      return 'bg-yellow-50 text-yellow-700 border-yellow-200';
    default:
      return 'bg-slate-50';
  }
};

// --- PriceTypeControls ---
function PriceTypeControls({
  selectedCurrencies,
  onChange,
  i18n,
}: {
  selectedCurrencies: string[];
  onChange: (currencies: string[]) => void;
  i18n: any;
}) {
  const { currencies, loading } = usePriceMetadata();

  if (loading) return <div className="text-xs text-slate-500">{i18n.loading}</div>;

  function handleToggle(code: string) {
    if (selectedCurrencies.includes(code)) {
      onChange(selectedCurrencies.filter(c => c !== code));
    } else {
      onChange([...selectedCurrencies, code]);
    }
  }

  return (
    <div className="pt-2">
      <Label>{i18n.form.fields.currencies}</Label>
      <div className="mt-1 flex flex-wrap gap-2">
        {currencies.map(c => (
          <label key={c.iso_code} className="flex items-center gap-1 text-sm">
            <input
              type="checkbox"
              checked={selectedCurrencies.includes(c.iso_code)}
              onChange={() => handleToggle(c.iso_code)}
            />
            {c.iso_code} <span className="text-slate-400">({c.symbol})</span>
          </label>
        ))}
      </div>
    </div>
  );
}

// --- MeasurementTypeControls ---
const DEFAULT_UNITS = ['kg', 'g', 'lb', 'oz', 'cm', 'mm', 'm', 'in', 'ft', 'l', 'ml'];

function MeasurementTypeControls({
  selectedUnits,
  onChange,
  i18n,
}: {
  selectedUnits: string[];
  onChange: (units: string[]) => void;
  i18n: any;
}) {
  function handleToggle(unit: string) {
    if (selectedUnits.includes(unit)) {
      onChange(selectedUnits.filter(u => u !== unit));
    } else {
      onChange([...selectedUnits, unit]);
    }
  }

  return (
    <div className="pt-2">
      <Label>{i18n.form.fields.units}</Label>
      <div className="mt-1 flex flex-wrap gap-2">
        {DEFAULT_UNITS.map(unit => (
          <label key={unit} className="flex items-center gap-1 text-sm">
            <input
              type="checkbox"
              checked={selectedUnits.includes(unit)}
              onChange={() => handleToggle(unit)}
            />
            {unit}
          </label>
        ))}
      </div>
      <div className="mt-2">
        <Label htmlFor="custom-units" className="text-xs">
          {i18n.form.fields.customUnits}
        </Label>
        <input
          id="custom-units"
          type="text"
          className="mt-1 w-full rounded border px-2 py-1 text-xs"
          placeholder={i18n.form.fields.customUnitsPlaceholder}
          value={selectedUnits.filter(u => !DEFAULT_UNITS.includes(u)).join(', ')}
          onChange={e => {
            const custom = e.target.value
              .split(',')
              .map(s => s.trim())
              .filter(Boolean);
            const merged = [...DEFAULT_UNITS.filter(u => selectedUnits.includes(u)), ...custom];
            onChange(merged);
          }}
        />
      </div>
    </div>
  );
}

function OptionListEditor({
  options,
  onChange,
  i18n,
}: {
  options: string[];
  onChange: (options: string[]) => void;
  i18n: any;
}) {
  const [input, setInput] = useState('');

  function addOption() {
    const val = input.trim();
    if (val && !options.includes(val)) {
      onChange([...options, val]);
      setInput('');
    }
  }

  function removeOption(idx: number) {
    onChange(options.filter((_, i) => i !== idx));
  }

  function editOption(idx: number, val: string) {
    const newOpts = [...options];
    newOpts[idx] = val;
    onChange(newOpts);
  }

  return (
    <div className="pt-2">
      <Label>{i18n.form.fields.options}</Label>
      <div className="mt-1 flex gap-2">
        <Input
          value={input}
          onChange={e => setInput(e.target.value)}
          onKeyDown={e => {
            if (e.key === 'Enter') addOption();
          }}
          placeholder={i18n.form.fields.optionsPlaceholder}
        />
        <Button type="button" onClick={addOption}>
          {i18n.actions.addOption}
        </Button>
      </div>
      <ul className="mt-2 space-y-1">
        {options.map((opt, i) => (
          <li key={i} className="flex items-center gap-2">
            <Input value={opt} onChange={e => editOption(i, e.target.value)} className="w-40" />
            <Button type="button" variant="ghost" onClick={() => removeOption(i)}>
              Remove
            </Button>
          </li>
        ))}
      </ul>
    </div>
  );
}

function AttributesPage() {
  const queryClient = useQueryClient();
  const invalidateAttributesCache = useInvalidateAttributesOnlyCache();
  const { user } = useAuth();
  const i18n = useAttributesTranslations();
  const { t } = useTranslation('translation');

  // Enhanced modal state
  const [isEnhancedModalOpen, setIsEnhancedModalOpen] = useState(false);
  const [editingAttribute, setEditingAttribute] = useState<EnhancedAttribute | null>(null);
  const [modalMode, setModalMode] = useState<'create' | 'edit'>('create');

  // Legacy state (kept for backward compatibility with existing code)
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedAttribute, setSelectedAttribute] = useState<EnhancedAttribute | null>(null);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);

  // Enhanced hooks for attributes
  const {
    data: attributes,
    isLoading: isLoadingAttributes,
    error: attributesError,
    refetch: refetchAttributes,
  } = useQuery({
    queryKey: ['attributes'],
    queryFn: async () => {
      const response = await axiosInstance.get(paths.attributes.root());
      return response.data;
    },
    staleTime: 300000, // 5 minutes
    retry: (failureCount, error) => {
      if (error instanceof Error && error.message.includes('permission')) {
        return false;
      }
      return failureCount < 3;
    },
  });

  // Query for effective attributes usage
  const { data: effectiveAttributesUsage } = useQuery({
    queryKey: ['effective-attributes-usage'],
    queryFn: async () => {
      const response = await axiosInstance.get('/api/attributes/usage/');
      // Convert to a Map for O(1) lookup
      const usageMap = new Map();
      response.data.forEach((item: any) => {
        usageMap.set(item.attribute_id, {
          hasValue: item.has_value,
          productCount: item.product_count,
          valueCount: item.value_count,
        });
      });
      return usageMap;
    },
    enabled: !!attributes && Array.isArray(attributes) && attributes.length > 0,
  });

  // Check if user is staff
  const isStaff = true;

  const [formValues, setFormValues] = useState<AttributeFormData>({
    code: '',
    label: '',
    data_type: 'text',
    is_localisable: false,
    is_scopable: false,
    is_required: false,
    validation_rule: '',
    allowed_units: [],
    default_unit: '',
    default_currency: '',
    allowed_html_tags: [],
    allowed_file_types: [],
    max_file_size: 10485760,
    allow_negative_numbers: true,
    options: [],
  });
  const [formErrors, setFormErrors] = useState<Record<string, string>>({});

  // Create attribute mutation
  const createMutation = useMutation({
    mutationFn: async (data: AttributeFormData) => {
      const response = await axiosInstance.post(paths.attributes.root(), data, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem('access_token')}`,
        },
      });
      return response.data;
    },
    onSuccess: () => {
      invalidateAttributesCache();
      toast.success(i18n.messages.createSuccess);
      setIsEnhancedModalOpen(false);
      resetForm();
    },
    onError: (error: any) => {
      const errorMessage = error.response?.data?.detail || i18n.messages.createError;
      toast.error(errorMessage);

      // Handle validation errors
      if (error.response?.status === 400 && error.response?.data) {
        setFormErrors(error.response.data);
      }
    },
  });

  // Update attribute mutation
  const updateMutation = useMutation({
    mutationFn: async ({ id, data }: { id: number; data: AttributeFormData }) => {
      const response = await axiosInstance.put(`${paths.attributes.root()}${id}/`, data, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem('access_token')}`,
        },
      });
      return response.data;
    },
    onSuccess: () => {
      invalidateAttributesCache();
      toast.success(i18n.messages.updateSuccess);
      setIsEnhancedModalOpen(false);
      resetForm();
    },
    onError: (error: any) => {
      const errorMessage = error.response?.data?.detail || i18n.messages.updateError;
      toast.error(errorMessage);

      // Handle validation errors
      if (error.response?.status === 400 && error.response?.data) {
        setFormErrors(error.response.data);
      }
    },
  });

  // Delete attribute mutation
  const deleteMutation = useMutation({
    mutationFn: async (attributeId: number) => {
      const response = await axiosInstance.delete(`${paths.attributes.root()}${attributeId}/`, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem('access_token')}`,
        },
      });
      return response.data;
    },
    onSuccess: () => {
      invalidateAttributesCache();
      toast.success(i18n.messages.deleteSuccess);
    },
    onError: (error: any) => {
      logError(error, {
        component: 'AttributesPage',
        action: 'deleteAttribute',
        metadata: { attributeId: error.attributeId },
      });
      const errorMessage = error.response?.data?.detail || i18n.messages.deleteError;
      toast.error(errorMessage);
    },
  });

  // Reset form values
  const resetForm = () => {
    setFormValues({
      code: '',
      label: '',
      data_type: 'text',
      is_localisable: false,
      is_scopable: false,
      is_required: false,
      validation_rule: '',
      allowed_units: [],
      default_unit: '',
      default_currency: '',
      allowed_html_tags: [],
      allowed_file_types: [],
      max_file_size: 10485760,
      allow_negative_numbers: true,
      options: [],
    });
    setFormErrors({});
  };

  // Enhanced create/edit handlers
  const handleCreateAttribute = () => {
    setEditingAttribute(null);
    setModalMode('create');
    setIsEnhancedModalOpen(true);
  };

  const handleEditAttribute = (attribute: EnhancedAttribute) => {
    setEditingAttribute(attribute);
    setModalMode('edit');
    setIsEnhancedModalOpen(true);
  };

  // Enhanced submit handler for the new modal
  const handleEnhancedSubmit = async (data: AttributeFormData) => {
    try {
      if (modalMode === 'create') {
        await createMutation.mutateAsync(data);
      } else if (editingAttribute) {
        await updateMutation.mutateAsync({ id: editingAttribute.id, data });
      }
    } catch (error) {
      logError(error as Error, {
        component: 'AttributesPage',
        action: 'submitAttributeForm',
        metadata: { mode: modalMode, attributeId: editingAttribute?.id },
      });
      throw error; // Re-throw to be handled by the modal
    }
  };

  // Handle attribute deletion
  const handleDelete = (attribute: EnhancedAttribute) => {
    if (attribute.is_core) {
      toast.error(i18n.table.tooltips.coreAttribute);
      return;
    }

    if (attribute.in_use) {
      toast.error(i18n.table.tooltips.inUseAttribute);
      return;
    }

    deleteMutation.mutate(attribute.id);
  };

  // Categorize attributes by data type for display
  const categorizedAttributes =
    attributes && Array.isArray(attributes)
      ? attributes.reduce((acc: Record<string, EnhancedAttribute[]>, attr: EnhancedAttribute) => {
          const category = attr.data_type;
          if (!acc[category]) {
            acc[category] = [];
          }
          acc[category].push(attr);
          return acc;
        }, {})
      : {};

  if (!ENABLE_CUSTOM_ATTRIBUTES) {
    return null;
  }

  return (
    <div className="space-y-6">
      {/* Create Attribute button */}
      <div className="flex justify-end">
        <Button onClick={handleCreateAttribute} disabled={!ENABLE_CUSTOM_ATTRIBUTES}>
          <PlusCircle className="mr-2 size-4" />
          {i18n.enhanced.modal.buttons.createAttribute}
        </Button>
      </div>

      {!ENABLE_CUSTOM_ATTRIBUTES && (
        <div className="rounded-lg border border-yellow-200 bg-yellow-50 p-4">
          <div className="flex">
            <AlertCircle className="size-5 text-yellow-400" />
            <div className="ml-3">
              <h3 className="text-sm font-medium text-yellow-800">Custom Attributes Disabled</h3>
              <div className="mt-2 text-sm text-yellow-700">
                <p>
                  Custom attribute creation is currently disabled. Contact your administrator to
                  enable this feature.
                </p>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Enhanced attributes table */}
      <Card>
        <CardHeader>
          <CardTitle>{i18n.title}</CardTitle>
          <CardDescription>{i18n.description}</CardDescription>
        </CardHeader>
        <CardContent>
          {isLoadingAttributes ? (
            <div className="space-y-3">
              {[...Array(5)].map((_, i) => (
                <Skeleton key={i} className="h-12 w-full" />
              ))}
            </div>
          ) : attributesError ? (
            <div className="flex items-center justify-center py-8">
              <div className="text-center">
                <AlertCircle className="mx-auto size-8 text-red-500" />
                <p className="mt-2 text-sm text-muted-foreground">{i18n.messages.loadError}</p>
                <Button variant="outline" onClick={() => refetchAttributes()} className="mt-2">
                  {i18n.actions.retry}
                </Button>
              </div>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>{i18n.table.headers.label}</TableHead>
                  <TableHead>{i18n.table.headers.code}</TableHead>
                  <TableHead>{i18n.table.headers.type}</TableHead>
                  <TableHead>
                    {i18n.table.configuration?.types ?? i18n.table.headers.usage}
                  </TableHead>
                  <TableHead>{i18n.table.headers?.label ?? i18n.table.headers.type}</TableHead>
                  <TableHead className="text-right">{i18n.table.headers.actions}</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {(attributes || []).map((attribute: EnhancedAttribute) => {
                  const TypeIcon = getAttributeTypeIcon(attribute.data_type);

                  return (
                    <TableRow key={attribute.id}>
                      <TableCell>
                        <div>
                          <div className="font-medium">{attribute.label}</div>
                          {attribute.is_core && (
                            <Badge variant="secondary" className="mt-1 text-xs">
                              {i18n.table.badges.core}
                            </Badge>
                          )}
                        </div>
                      </TableCell>
                      <TableCell>
                        <code className="rounded bg-muted px-2 py-1 text-xs">{attribute.code}</code>
                      </TableCell>
                      <TableCell>
                        <Badge
                          variant="outline"
                          className={`${getAttributeTypeColor(attribute.data_type)} capitalize`}
                        >
                          <TypeIcon className="mr-1 size-3" />
                          {translateAttributeType(attribute.data_type, t)}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        <div className="flex flex-wrap gap-1">
                          {attribute.is_required && (
                            <Badge variant="destructive" className="text-xs">
                              {i18n.table.badges.required}
                            </Badge>
                          )}
                          {attribute.is_localisable && (
                            <TooltipProvider>
                              <Tooltip>
                                <TooltipTrigger>
                                  <Badge variant="outline" className="text-xs">
                                    <Globe className="mr-1 size-3" />
                                    {i18n.table.badges.locale}
                                  </Badge>
                                </TooltipTrigger>
                                <TooltipContent>
                                  <p>{i18n.table.tooltips.localeSupport}</p>
                                </TooltipContent>
                              </Tooltip>
                            </TooltipProvider>
                          )}
                          {attribute.is_scopable && (
                            <TooltipProvider>
                              <Tooltip>
                                <TooltipTrigger>
                                  <Badge variant="outline" className="text-xs">
                                    <Monitor className="mr-1 size-3" />
                                    {i18n.table.badges.channel}
                                  </Badge>
                                </TooltipTrigger>
                                <TooltipContent>
                                  <p>{i18n.table.tooltips.channelSupport}</p>
                                </TooltipContent>
                              </Tooltip>
                            </TooltipProvider>
                          )}
                        </div>
                      </TableCell>
                      <TableCell>
                        <div className="space-y-1">
                          {/* Select attribute options */}
                          {attribute.data_type === 'select' && attribute.options && (
                            <div className="text-xs text-muted-foreground">
                              {i18n.table.configuration.options}{' '}
                              {attribute.options
                                .slice(0, 3)
                                .map(opt => opt.label)
                                .join(', ')}
                              {attribute.options.length > 3 &&
                                ` +${attribute.options.length - 3} more`}
                              {attribute.options.length === 0 && (
                                <span className="text-red-600">
                                  {i18n.table.configuration.noOptions}
                                </span>
                              )}
                            </div>
                          )}

                          {/* Measurement units */}
                          {attribute.data_type === 'measurement' && attribute.allowed_units && (
                            <div className="text-xs text-muted-foreground">
                              {i18n.table.configuration.units}{' '}
                              {attribute.allowed_units.slice(0, 3).join(', ')}
                              {attribute.allowed_units.length > 3 &&
                                ` +${attribute.allowed_units.length - 3}`}
                            </div>
                          )}

                          {/* Price currency */}
                          {attribute.data_type === 'price' && attribute.default_currency && (
                            <div className="text-xs text-muted-foreground">
                              {i18n.table.configuration.currency} {attribute.default_currency}
                            </div>
                          )}

                          {/* Validation rule */}
                          {attribute.validation_rule && (
                            <div className="text-xs text-muted-foreground">
                              {i18n.table.configuration.validationRule}
                            </div>
                          )}

                          {/* File types for media */}
                          {attribute.data_type === 'media' && attribute.allowed_file_types && (
                            <div className="text-xs text-muted-foreground">
                              {i18n.table.configuration.types}{' '}
                              {attribute.allowed_file_types.slice(0, 2).join(', ')}
                              {attribute.allowed_file_types.length > 2 &&
                                ` +${attribute.allowed_file_types.length - 2}`}
                            </div>
                          )}
                        </div>
                      </TableCell>
                      <TableCell className="text-right">
                        <div className="flex items-center justify-end space-x-2">
                          <TooltipProvider>
                            <Tooltip>
                              <TooltipTrigger asChild>
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => handleEditAttribute(attribute)}
                                  disabled={updateMutation.isPending}
                                >
                                  <Pencil className="size-4" />
                                </Button>
                              </TooltipTrigger>
                              <TooltipContent>
                                <p>{i18n.table.tooltips.editAttribute}</p>
                              </TooltipContent>
                            </Tooltip>
                          </TooltipProvider>

                          <AlertDialog>
                            <AlertDialogTrigger asChild>
                              <Button
                                variant="ghost"
                                size="sm"
                                disabled={
                                  deleteMutation.isPending || attribute.is_core || attribute.in_use
                                }
                              >
                                <Trash className="size-4" />
                              </Button>
                            </AlertDialogTrigger>
                            <AlertDialogContent>
                              <AlertDialogHeader>
                                <AlertDialogTitle>{i18n.dialogs.deleteTitle}</AlertDialogTitle>
                                <AlertDialogDescription>
                                  Are you sure you want to delete "{attribute.label}"? This action
                                  cannot be undone.
                                  {attribute.in_use && (
                                    <div className="mt-2 text-red-600">
                                      Warning: This attribute is currently in use by products.
                                    </div>
                                  )}
                                </AlertDialogDescription>
                              </AlertDialogHeader>
                              <AlertDialogFooter>
                                <AlertDialogCancel>{i18n.actions.cancel}</AlertDialogCancel>
                                <AlertDialogAction
                                  onClick={() => handleDelete(attribute)}
                                  className="bg-red-600 hover:bg-red-700"
                                >
                                  Delete
                                </AlertDialogAction>
                              </AlertDialogFooter>
                            </AlertDialogContent>
                          </AlertDialog>
                        </div>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* Enhanced Attribute Modal */}
      <EnhancedAttributeModal
        isOpen={isEnhancedModalOpen}
        onOpenChange={setIsEnhancedModalOpen}
        onSubmit={handleEnhancedSubmit}
        initialData={
          editingAttribute
            ? {
                code: editingAttribute.code,
                label: editingAttribute.label,
                data_type: editingAttribute.data_type,
                is_localisable: editingAttribute.is_localisable,
                is_scopable: editingAttribute.is_scopable,
                is_required: editingAttribute.is_required,
                validation_rule: editingAttribute.validation_rule || '',
                allowed_units: editingAttribute.allowed_units || [],
                default_unit: editingAttribute.default_unit || '',
                default_currency: editingAttribute.default_currency || '',
                allowed_html_tags: editingAttribute.allowed_html_tags || [],
                allowed_file_types: editingAttribute.allowed_file_types || [],
                max_file_size: editingAttribute.max_file_size || 10485760,
                allow_negative_numbers: editingAttribute.allow_negative_numbers ?? true,
                options: editingAttribute.options || [],
              }
            : undefined
        }
        mode={modalMode}
        isPending={createMutation.isPending || updateMutation.isPending}
      />
    </div>
  );
}

export default AttributesPage;
